import { formatScore, isWeekly, type ScoreMetric, type ScoreSection } from './scoreboard';

export const headlineKeys: Record<string, string> = {
  'linkedin-outbound': 'out-calls',
  'linkedin-ads': 'ads-spend',
  'vsl-site': 'site-sessions',
  manychat: 'mc-leads',
  pipeline: 'pipe-won',
};
export function headline(section: ScoreSection) {
  const raw = section.metrics.filter((m) => m.is_active && m.value_type !== 'rate');
  return raw.find((m) => m.seed_key === headlineKeys[section.slug ?? '']) ?? raw[0];
}
/**
 * Fields due for the selected day. A "(weekly)" field is entered once a week, so on a section
 * that has any, only the daily fields count; otherwise the server's own count is used.
 */
export function entryCounts(section: ScoreSection): { entered: number; total: number } {
  if (!section.metrics.some((m) => m.is_active && isWeekly(m.label)))
    return { entered: section.entered_fields, total: section.total_fields };
  const due = section.metrics.filter(
    (m) => m.is_active && m.value_type !== 'rate' && !isWeekly(m.label),
  );
  return {
    entered: due.filter((m) => (m.totals.selected?.entered_days ?? 0) > 0).length,
    total: due.length,
  };
}
export function entryStatus(section: ScoreSection) {
  const { entered, total } = entryCounts(section);
  if (!entered) return 'Not entered yet';
  return entered === total ? 'Entered' : 'Partly entered';
}
export function editRights(section: ScoreSection) {
  if (section.is_open) return 'Open · anyone on TAP can edit';
  return section.owner_name
    ? `${section.owner_name} · ${section.owner_name} and Kyu can edit`
    : 'Unassigned · Kyu can edit';
}
export function momentum(metric: ScoreMetric | undefined): number | null {
  const current = metric?.totals.seven;
  const previous = metric?.totals.previous;
  if (
    current?.value == null ||
    previous?.value == null ||
    previous.value === 0 ||
    current.entered_days < 5 ||
    previous.entered_days < 5
  )
    return null;
  return (current.value - previous.value) / previous.value;
}
export function momentumText(metric: ScoreMetric | undefined) {
  const change = momentum(metric);
  return change === null
    ? 'Not enough comparable days yet'
    : `${change > 0 ? '▲' : change < 0 ? '▼' : '→'} ${Math.abs(change * 100).toFixed(0)}% vs previous 7 days`;
}
/** Ordered evidence, not an interpretation of whether more is better. */
export function standouts(sections: ScoreSection[]): string[] {
  const warnings: string[] = [];
  for (const section of sections)
    for (const rate of section.metrics) {
      if (rate.value_type !== 'rate' || rate.rate_format !== 'money') continue;
      const numerator = section.metrics.find((m) => m.id === rate.numerator_id);
      const denominator = section.metrics.find((m) => m.id === rate.denominator_id);
      if (
        numerator?.value_type === 'money' &&
        denominator?.value_type === 'count' &&
        (numerator.totals.seven?.value ?? 0) > 0 &&
        denominator.totals.seven?.value === 0
      ) {
        warnings.push(
          `${section.name} · ${rate.label}: spent ${formatScore(numerator.totals.seven?.value, numerator)} over 7 days with no ${denominator.label.toLowerCase()}`,
        );
      }
    }
  const missing = sections
    .filter((s) => s.entered_fields === 0)
    .map((s) => `${s.name}: nothing entered for the selected day.`);
  const changes = sections.flatMap((s) => {
    if (s.kind === 'snapshot') return [];
    const best = s.metrics
      .filter((m) => m.value_type !== 'rate')
      .map((m) => ({ m, change: momentum(m) }))
      .filter(
        (x): x is { m: ScoreMetric; change: number } =>
          x.change !== null && Math.abs(x.change) >= 0.15,
      )
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change) || a.m.position - b.m.position)[0];
    return best ? [`${s.name} · ${best.m.label}: ${momentumText(best.m)}.`] : [];
  });
  return [...warnings, ...missing, ...changes].slice(0, 4);
}
export interface AdRow {
  name: string;
  spend?: ScoreMetric;
  views?: ScoreMetric;
  clicks?: ScoreMetric;
  cpc?: ScoreMetric;
}
export function adRows(section: ScoreSection): AdRow[] {
  const rows = new Map<string, AdRow>();
  const keys = {
    Spend: 'spend',
    'Ad views': 'views',
    'Link clicks': 'clicks',
    'Cost per click': 'cpc',
  } as const;
  for (const field of section.metrics) {
    const split = field.label.lastIndexOf(' · ');
    if (split < 1) continue;
    const name = field.label.slice(0, split),
      label = field.label.slice(split + 3);
    const key = keys[label as keyof typeof keys];
    if (!key || (key === 'cpc' && field.value_type !== 'rate')) continue;
    const row = rows.get(name) ?? { name };
    row[key] = field;
    rows.set(name, row);
  }
  return [...rows.values()].sort((a, b) => {
    const aClicks = a.clicks?.totals.seven?.value ?? 0,
      bClicks = b.clicks?.totals.seven?.value ?? 0;
    if (aClicks > 0 !== bClicks > 0) return aClicks > 0 ? -1 : 1;
    return (
      (a.cpc?.totals.seven?.value ?? Infinity) - (b.cpc?.totals.seven?.value ?? Infinity) ||
      a.name.localeCompare(b.name)
    );
  });
}
export function loadOpenSections(viewer: string): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(`score-details:${viewer}`) ?? '[]');
    return Array.isArray(value) ? value.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
export function saveOpenSections(viewer: string, ids: string[]) {
  try {
    localStorage.setItem(`score-details:${viewer}`, JSON.stringify(ids));
  } catch {
    /* Private browsing still works. */
  }
}
